import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/custom/loader';
import { FormCheck, FormField } from '@/components/custom/form';
import { Button } from '@/components/ui/button';
import { ModalTaxonomy } from '@/components/custom/modal/modal-taxonomy';
import { cn } from '@/lib/utils';

/**
 * Componente que gerencia a exibição e a seleção (via modal) de taxonomias (matérias).
 *
 * @param {object} props
 * @param {boolean} props.isLoading - Indica se a lista completa de taxonomias está sendo carregada.
 * @param {Array} props.taxonomies - A lista completa de taxonomias (para o Modal).
 * @param {Array} props.selectedTaxonomies - As taxonomias atualmente selecionadas (form.values.selectedTaxonomies).
 * @param {function} props.setFieldValue - Função do Formik para atualizar o campo 'selectedTaxonomies'.
 * @param {object} props.errors - Objeto de erros do Formik.
 * @param {object} props.touched - Objeto de touched do Formik.
 * @param {boolean} props.isOpenModalTaxonomies - Estado do modal.
 * @param {function} props.setIsOpenModalTaxonomies - Setter para o estado do modal.
 */
export function FormTaxonomy({ className, isLoading, taxonomies, selectedTaxonomies, setFieldValue, errors, touched, isOpenModalTaxonomies, setIsOpenModalTaxonomies, buttonVariant = 'outline', ...props }) {
  const { t } = useTranslation();

  // Lógica para remover uma taxonomia selecionada ao desmarcar o FormCheck
  const handleRemoveTaxonomy = taxonomyToRemove => {
    const newSelectedTaxonomies = selectedTaxonomies.filter(t2 => taxonomyToRemove.id !== t2.id);
    setFieldValue('selectedTaxonomies', newSelectedTaxonomies);
  };

  return (
    <>
      <FormField
        label={t('textsCommon.matters')}
        hasError={errors.selectedTaxonomies && touched.selectedTaxonomies}
        errorText={errors.selectedTaxonomies}
        className={cn('c-filter-check', 'mb-3', className)}
        {...props}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {/* 1. Exibição das Matérias Selecionadas */}
            {selectedTaxonomies.length > 0 && (
              <div className="!mb-3 flex flex-wrap gap-x-5 gap-y-2">
                {selectedTaxonomies.map(t => (
                  <FormCheck
                    key={t.id}
                    label={t.name}
                    type="checkbox"
                    checked
                    onCheckedChange={() => handleRemoveTaxonomy(t)}
                  />
                ))}
              </div>
            )}

            {/* 2. Botão para Abrir o Modal de Seleção */}
            <div>
              <Button
                variant={buttonVariant}
                type="button"
                onClick={() => setIsOpenModalTaxonomies(true)}
              >
                {selectedTaxonomies.length > 0 ? 'Editar matérias' : t('textsCommon.buttonAddMatters')}
              </Button>
            </div>
          </>
        )}
      </FormField>

      {/* 3. Modal de Seleção de Taxonomias */}
      {taxonomies.length > 0 && isOpenModalTaxonomies && (
        <ModalTaxonomy
          taxonomies={taxonomies}
          selectedTaxonomies={selectedTaxonomies}
          setFieldValue={setFieldValue}
          open={taxonomies.length > 0 && isOpenModalTaxonomies}
          onOpenChange={setIsOpenModalTaxonomies}
          closeModal={() => setIsOpenModalTaxonomies(false)}
        />
      )}
    </>
  );
}
